import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { API_URL } from "../services/api";
import VerificationGate from "../components/VerificationGate";

function Products() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") || "active";

  const [activeTab, setActiveTab] = useState(initialTab); // 'active' | 'pending' | 'drafts' | 'rejected'
  const [products, setProducts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [limitInfo, setLimitInfo] = useState({ count: 0, limit: 0, planName: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const getFullImageUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    if (path.startsWith("public")) return `${API_URL}/${path}`;
    return `${API_URL}/${path.replace(/^\//, "")}`;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [prodRes, draftRes, dashRes, readinessRes] = await Promise.all([
        api.get("/vendor/products"),
        api.get("/vendor/products/drafts"),
        api.get("/vendor/dashboard"),
        api.get("/vendor/verification/status").catch(() => ({ data: null }))
      ]);

      setProducts(prodRes.data.products || []);
      setDrafts(draftRes.data.drafts || []);
      setReadiness(readinessRes.data || null);
      setLimitInfo({
        count: dashRes.data.productCount || 0,
        limit: dashRes.data.productLimit || 0,
        planName: dashRes.data.currentPlan?.name || "Basic"
      });
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to load products and drafts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    setActionLoadingId(id);
    try {
      await api.delete(`/vendor/products/${id}`);
      setProducts(products.filter((p) => p._id !== id));
      setLimitInfo((prev) => ({ ...prev, count: Math.max(0, prev.count - 1) }));
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to delete product.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteDraft = async (id) => {
    if (!window.confirm("Are you sure you want to discard this draft? This cannot be undone.")) return;
    setActionLoadingId(id);
    try {
      await api.delete(`/vendor/products/drafts/${id}`);
      setDrafts(drafts.filter((d) => d._id !== id));
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to discard draft.");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Filter products by status
  const activeProducts = products.filter((p) => (p.approvalStatus || "approved") === "approved");
  const pendingProducts = products.filter((p) => p.approvalStatus === "pending");
  const rejectedProducts = products.filter((p) => p.approvalStatus === "rejected");
  const blockedProducts = products.filter((p) => p.approvalStatus === "blocked");

  // Review request modal states
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewTargetProduct, setReviewTargetProduct] = useState(null);
  const [reviewMessage, setReviewMessage] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  const handleSubmitReviewRequest = async (e) => {
    if (e) e.preventDefault();
    if (!reviewMessage.trim() || !reviewTargetProduct) return;

    try {
      setSubmittingReview(true);
      await api.post(`/vendor/products/${reviewTargetProduct._id}/review-request`, {
        vendorMessage: reviewMessage.trim(),
      });
      alert("Review request submitted! Admin will evaluate your changes.");
      setReviewModalOpen(false);
      setReviewTargetProduct(null);
      setReviewMessage("");
      fetchData();
    } catch (err) {
      alert(err.response?.data?.msg || "Could not submit review request.");
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "28px", paddingBottom: "20px", borderBottom: "1px solid var(--border)" }}>
          <div>
            <div className="skeleton" style={{ height: "28px", width: "160px", marginBottom: "8px" }} />
            <div className="skeleton" style={{ height: "16px", width: "240px" }} />
          </div>
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="skeleton" style={{ height: "70px", marginBottom: "8px", borderRadius: "8px" }} />
        ))}
      </div>
    );
  }

  if (readiness && !readiness.isEligible && products.length === 0 && drafts.length === 0) {
    return <VerificationGate readiness={readiness} title="Complete Seller Verification to Start Listing Products" />;
  }

  const isLimitReached = limitInfo.limit > 0 && limitInfo.count >= limitInfo.limit;
  const productPct = limitInfo.limit > 0 ? Math.min(100, Math.round((limitInfo.count / limitInfo.limit) * 100)) : 0;
  const remainingSlots = Math.max(0, limitInfo.limit - limitInfo.count);

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h2>Products &amp; Catalogue</h2>
          <p>Manage active products, review queues, and unfinished drafts.</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-muted)" }}>
            {limitInfo.limit > 0 ? `${limitInfo.count}/${limitInfo.limit} published slots used` : `${limitInfo.count} published products`}
          </span>
          <Link to="/vendor/products/new" className="btn btn-primary" style={{ fontSize: "13px" }}>
            + Add Product
          </Link>
        </div>
      </div>

      {/* Seller Verification Locked Alert Banner */}
      {readiness && !readiness.isEligible && (
        <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: "10px", padding: "14px 18px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <strong style={{ color: "#991b1b", display: "block", fontSize: "13.5px" }}>🔒 Product Listing Locked</strong>
            <span style={{ color: "#b91c1c", fontSize: "12.5px" }}>Complete official identity (Aadhaar & PAN) and bank payout verification to unlock product creation and submissions.</span>
          </div>
          <Link to="/vendor/verification" className="btn btn-primary" style={{ fontSize: "12.5px", padding: "7px 16px" }}>
            Complete Verification &rarr;
          </Link>
        </div>
      )}

      {/* Plan Usage Progress Track */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "12px", color: "var(--text-muted)", fontWeight: "600" }}>
          <span>Published Products Capacity ({limitInfo.planName || "Basic"} Plan)</span>
          <span>
            {limitInfo.limit > 0
              ? `${productPct}% used (${remainingSlots} publishing slots available)`
              : "Drafts do not consume plan quota"}
          </span>
        </div>
        <div className="usage-bar-track">
          <div className={"usage-bar-fill" + (productPct >= 100 ? " danger" : productPct >= 70 ? " warning" : "")} style={{ width: productPct + "%" }} />
        </div>
        <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "4px" }}>
          (Drafts do not consume plan quota)
        </div>
      </div>

      {isLimitReached && (
        <div style={{ background: "#fffbeb", border: "1px solid #fed7aa", borderRadius: "10px", padding: "14px 18px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
          <div style={{ fontSize: "14px", color: "#92400e" }}>
            <strong>Plan Limit Reached</strong> &mdash; You have reached {limitInfo.limit} published products on the {limitInfo.planName} plan. You can still save drafts freely, but upgrading will unlock higher publishing limits.
          </div>
          <Link to="/vendor/subscription" className="btn btn-primary" style={{ fontSize: "12px", padding: "7px 16px" }}>
            Upgrade Plan
          </Link>
        </div>
      )}

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fecaca", borderRadius: "8px", padding: "14px", color: "#dc2626", marginBottom: "16px" }}>
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="vendor-tabs-scroll" style={{ display: "flex", borderBottom: "1.5px solid #edf0f4", marginBottom: "20px", gap: "20px" }}>
        <button
          type="button"
          onClick={() => setActiveTab("active")}
          style={{
            background: "transparent",
            border: "none",
            borderBottom: activeTab === "active" ? "3px solid var(--primary)" : "3px solid transparent",
            padding: "10px 4px",
            fontSize: "14px",
            fontWeight: activeTab === "active" ? "800" : "600",
            color: activeTab === "active" ? "var(--primary)" : "var(--text-muted)",
            cursor: "pointer"
          }}
        >
          Active Listings ({activeProducts.length})
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("pending")}
          style={{
            background: "transparent",
            border: "none",
            borderBottom: activeTab === "pending" ? "3px solid var(--primary)" : "3px solid transparent",
            padding: "10px 4px",
            fontSize: "14px",
            fontWeight: activeTab === "pending" ? "800" : "600",
            color: activeTab === "pending" ? "var(--primary)" : "var(--text-muted)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}
        >
          <span>Pending Review</span>
          {pendingProducts.length > 0 && (
            <span style={{ background: "#fef3c7", color: "#92400e", fontSize: "11px", fontWeight: "900", padding: "1px 7px", borderRadius: "99px" }}>
              {pendingProducts.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("drafts")}
          style={{
            background: "transparent",
            border: "none",
            borderBottom: activeTab === "drafts" ? "3px solid var(--primary)" : "3px solid transparent",
            padding: "10px 4px",
            fontSize: "14px",
            fontWeight: activeTab === "drafts" ? "800" : "600",
            color: activeTab === "drafts" ? "var(--primary)" : "var(--text-muted)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}
        >
          <span>Drafts</span>
          {drafts.length > 0 && (
            <span style={{ background: "#eff6ff", color: "#1e40af", fontSize: "11px", fontWeight: "900", padding: "1px 7px", borderRadius: "99px" }}>
              {drafts.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("rejected")}
          style={{
            background: "transparent",
            border: "none",
            borderBottom: activeTab === "rejected" ? "3px solid var(--primary)" : "3px solid transparent",
            padding: "10px 4px",
            fontSize: "14px",
            fontWeight: activeTab === "rejected" ? "800" : "600",
            color: activeTab === "rejected" ? "var(--primary)" : "var(--text-muted)",
            cursor: "pointer"
          }}
        >
          Needs Revision ({rejectedProducts.length})
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("blocked")}
          style={{
            background: "transparent",
            border: "none",
            borderBottom: activeTab === "blocked" ? "3px solid #dc2626" : "3px solid transparent",
            padding: "10px 4px",
            fontSize: "14px",
            fontWeight: activeTab === "blocked" ? "800" : "600",
            color: activeTab === "blocked" ? "#dc2626" : "var(--text-muted)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}
        >
          <span>⛔ Blocked</span>
          {blockedProducts.length > 0 && (
            <span style={{ background: "#fee2e2", color: "#991b1b", fontSize: "11px", fontWeight: "900", padding: "1px 7px", borderRadius: "99px" }}>
              {blockedProducts.length}
            </span>
          )}
        </button>
      </div>

      {/* TAB 1: ACTIVE PRODUCTS */}
      {activeTab === "active" && (
        <div className="card-brand" style={{ overflow: "hidden" }}>
          {activeProducts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">&#x1F4E6;</div>
              <h5>No Active Products</h5>
              <p>You have no approved products published on the marketplace yet.</p>
              <Link to="/vendor/products/new" className="btn btn-primary">
                + Add New Product
              </Link>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="product-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeProducts.map((product) => {
                    const headImg = product.mainImage?.[0]?.imageurl || "";
                    const imgSrc = getFullImageUrl(headImg);
                    return (
                      <tr key={product._id}>
                        <td>
                          {imgSrc ? (
                            <img src={imgSrc} alt={product.name} style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "8px", border: "1px solid var(--border)" }} />
                          ) : (
                            <div style={{ width: "56px", height: "56px", background: "var(--bg-cream)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>
                              &#x1F4F7;
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight: "700", color: "var(--dark)" }}>{product.name}</td>
                        <td style={{ color: "var(--text-muted)" }}>{product.category}</td>
                        <td style={{ fontWeight: "700" }}>₹{product.price}</td>
                        <td>
                          <span className={"status-pill " + (product.productStock > 0 ? "active" : "danger")}>
                            <span className="status-dot" />
                            {product.productStock} units
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <Link to={`/vendor/products/${product._id}/edit`} className="btn btn-outline-secondary btn-sm me-2" style={{ fontSize: "12px", padding: "6px 14px" }}>
                            Edit
                          </Link>
                          <button
                            onClick={() => handleDeleteProduct(product._id)}
                            className="btn btn-sm"
                            disabled={actionLoadingId === product._id}
                            style={{ fontSize: "12px", padding: "6px 14px", background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: "8px", fontWeight: "600" }}
                          >
                            {actionLoadingId === product._id ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: PENDING REVIEW */}
      {activeTab === "pending" && (
        <div className="card-brand" style={{ overflow: "hidden" }}>
          {pendingProducts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">⏳</div>
              <h5>No Products Pending Review</h5>
              <p>When you submit a new product listing, it appears here while our Admin verifies quality standards.</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="product-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Review Status</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingProducts.map((product) => {
                    const imgSrc = getFullImageUrl(product.mainImage?.[0]?.imageurl);
                    return (
                      <tr key={product._id}>
                        <td>
                          {imgSrc ? (
                            <img src={imgSrc} alt={product.name} style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "8px", border: "1px solid var(--border)" }} />
                          ) : (
                            <div style={{ width: "56px", height: "56px", background: "var(--bg-cream)", borderRadius: "8px", display: "grid", placeItems: "center" }}>
                              📷
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight: "700", color: "var(--dark)" }}>{product.name}</td>
                        <td style={{ color: "var(--text-muted)" }}>{product.category}</td>
                        <td style={{ fontWeight: "700" }}>₹{product.price}</td>
                        <td>
                          <span style={{ background: "#fef3c7", color: "#92400e", fontSize: "11.5px", fontWeight: "800", padding: "4px 10px", borderRadius: "99px" }}>
                            ● Under Review
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <Link to={`/vendor/products/${product._id}/edit`} className="btn btn-outline-secondary btn-sm" style={{ fontSize: "12px" }}>
                            View Details
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: UNFINISHED DRAFTS */}
      {activeTab === "drafts" && (
        <div>
          {drafts.length === 0 ? (
            <div className="card-brand empty-state">
              <div className="empty-state-icon">📝</div>
              <h5>No Unfinished Drafts</h5>
              <p>When you create a product, changes are saved automatically so you can resume anytime.</p>
              <Link to="/vendor/products/new" className="btn btn-primary">
                + Start New Product
              </Link>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "16px" }}>
              {drafts.map((draft) => {
                const imgSrc = getFullImageUrl(draft.mainImage?.[0]?.imageurl);
                const pct = draft.completionPercentage || 25;
                return (
                  <div
                    key={draft._id}
                    className="card-brand"
                    style={{
                      padding: "18px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      border: "1px solid #e0e4ea"
                    }}
                  >
                    <div>
                      {/* Top Strip */}
                      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "14px" }}>
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt="Draft Preview"
                            style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "10px", border: "1px solid #edf0f4" }}
                          />
                        ) : (
                          <div style={{ width: "64px", height: "64px", background: "#fbfaf8", borderRadius: "10px", display: "grid", placeItems: "center", border: "1px dashed #d1d5db", fontSize: "22px" }}>
                            📦
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong style={{ fontSize: "14.5px", color: "#111315", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {draft.name || "Untitled Product Draft"}
                          </strong>
                          <span style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginTop: "2px" }}>
                            {draft.category || "No category selected"}
                          </span>
                          <span style={{ fontSize: "12px", fontWeight: "700", color: "#111315", display: "block", marginTop: "2px" }}>
                            {draft.price > 0 ? `₹${draft.price}` : "Price not set"}
                          </span>
                        </div>
                      </div>

                      {/* Completion Progress Bar */}
                      <div style={{ marginBottom: "14px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11.5px", fontWeight: "700", marginBottom: "4px" }}>
                          <span style={{ color: "var(--text-muted)" }}>Completion</span>
                          <span style={{ color: pct === 100 ? "#16a34a" : "var(--primary)" }}>{pct}% complete</span>
                        </div>
                        <div style={{ background: "#f0f2f5", height: "6px", borderRadius: "99px", overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? "#16a34a" : "var(--primary)" }} />
                        </div>
                      </div>
                    </div>

                    {/* Footer & Actions */}
                    <div style={{ borderTop: "1px solid #edf0f4", paddingTop: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
                        Saved {new Date(draft.updatedAt).toLocaleDateString([], { month: "short", day: "numeric" })}
                      </span>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          type="button"
                          onClick={() => handleDeleteDraft(draft._id)}
                          className="btn btn-outline-secondary"
                          style={{ fontSize: "12px", padding: "5px 10px", color: "#dc2626", borderColor: "#fecaca" }}
                        >
                          Discard
                        </button>
                        <Link
                          to={`/vendor/products/new?draftId=${draft._id}`}
                          className="btn btn-primary"
                          style={{ fontSize: "12px", padding: "5px 14px", fontWeight: "800" }}
                        >
                          Continue &rarr;
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 4: REJECTED / NEEDS REVISION */}
      {activeTab === "rejected" && (
        <div className="card-brand" style={{ overflow: "hidden" }}>
          {rejectedProducts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">✅</div>
              <h5>No Rejected Products</h5>
              <p>All your products meet the marketplace guidelines.</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="product-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Admin Feedback / Reason</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rejectedProducts.map((product) => {
                    const imgSrc = getFullImageUrl(product.mainImage?.[0]?.imageurl);
                    return (
                      <tr key={product._id}>
                        <td>
                          {imgSrc ? (
                            <img src={imgSrc} alt={product.name} style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "8px", border: "1px solid var(--border)" }} />
                          ) : (
                            <div style={{ width: "56px", height: "56px", background: "var(--bg-cream)", borderRadius: "8px", display: "grid", placeItems: "center" }}>
                              📷
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight: "700", color: "var(--dark)" }}>{product.name}</td>
                        <td style={{ color: "var(--text-muted)" }}>{product.category}</td>
                        <td>
                          <div style={{ background: "#fee2e2", color: "#991b1b", padding: "6px 12px", borderRadius: "6px", fontSize: "12px" }}>
                            {product.rejectionReason || "Please review images and product description."}
                          </div>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <Link to={`/vendor/products/${product._id}/edit`} className="btn btn-primary btn-sm" style={{ fontSize: "12px", padding: "6px 14px" }}>
                            Edit &amp; Resubmit
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 5: BLOCKED PRODUCTS */}
      {activeTab === "blocked" && (
        <div className="card-brand" style={{ overflow: "hidden" }}>
          {blockedProducts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">✅</div>
              <h5>No Blocked Products</h5>
              <p>None of your listings are currently blocked by administration.</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="product-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Admin Reason</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blockedProducts.map((product) => {
                    const imgSrc = getFullImageUrl(product.mainImage?.[0]?.imageurl);
                    return (
                      <tr key={product._id}>
                        <td>
                          {imgSrc ? (
                            <img src={imgSrc} alt={product.name} style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "8px", border: "1px solid var(--border)" }} />
                          ) : (
                            <div style={{ width: "56px", height: "56px", background: "var(--bg-cream)", borderRadius: "8px", display: "grid", placeItems: "center" }}>
                              📷
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight: "700", color: "var(--dark)" }}>{product.name}</td>
                        <td style={{ color: "var(--text-muted)" }}>{product.category}</td>
                        <td>
                          <div style={{ background: "#fee2e2", color: "#991b1b", padding: "6px 12px", borderRadius: "6px", fontSize: "12px" }}>
                            <strong>⛔ Block Reason:</strong> {product.adminBlockReason || "This listing violates marketplace guidelines or compliance policies."}
                          </div>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            <button
                              type="button"
                              onClick={() => {
                                setReviewTargetProduct(product);
                                setReviewMessage("");
                                setReviewModalOpen(true);
                              }}
                              className="btn btn-primary btn-sm"
                              style={{ fontSize: "12px", padding: "6px 14px", fontWeight: "700" }}
                            >
                              Request Review
                            </button>
                            <Link
                              to="/vendor/messages"
                              className="btn btn-outline-secondary btn-sm"
                              style={{ fontSize: "12px", padding: "6px 12px" }}
                            >
                              ✉ Message Admin
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Review Request Modal */}
      {reviewModalOpen && reviewTargetProduct && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.65)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
          <div style={{ background: "#ffffff", borderRadius: "16px", maxWidth: "520px", width: "100%", padding: "26px", border: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f1f5f9", paddingBottom: "14px", marginBottom: "16px" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 900, color: "#0f172a" }}>
                  Request Product Review
                </h3>
                <p style={{ margin: "2px 0 0", fontSize: "12.5px", color: "#64748b" }}>
                  Product: <strong>{reviewTargetProduct.name}</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setReviewModalOpen(false);
                  setReviewTargetProduct(null);
                }}
                style={{ background: "#f1f5f9", border: "none", borderRadius: "8px", width: "32px", height: "32px", cursor: "pointer", fontWeight: 700 }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitReviewRequest}>
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 14px", fontSize: "12.5px", color: "#475569", marginBottom: "14px" }}>
                <strong>Current Block Reason:</strong> {reviewTargetProduct.adminBlockReason || "Policy review required."}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "12.5px", fontWeight: 800, color: "#334155", marginBottom: "6px" }}>
                  Explain what changes were made or why this listing should be unblocked *
                </label>
                <textarea
                  rows={4}
                  required
                  value={reviewMessage}
                  onChange={(e) => setReviewMessage(e.target.value)}
                  placeholder="Describe the corrections you made to title, image, price, or product description..."
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13px", outline: "none" }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  onClick={() => {
                    setReviewModalOpen(false);
                    setReviewTargetProduct(null);
                  }}
                  disabled={submittingReview}
                  className="btn btn-outline-secondary"
                  style={{ padding: "8px 18px", fontSize: "13px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingReview || !reviewMessage.trim()}
                  className="btn btn-primary"
                  style={{ padding: "8px 22px", fontSize: "13px", fontWeight: 700 }}
                >
                  {submittingReview ? "Submitting..." : "Submit Review Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;
